import React, { useCallback, useMemo, useState } from 'react';
import Heading from '../../../components/typography';
import FormSelect from '../../../components/dropdown/formSelect';
import { PageHeader } from '../../../constants/style/common.styled';
import { COLORS } from '../../../constants/style/color';
import { useSearchParticipants, useGetCsReasonCodes, useGetTradeOrderStatus } from '../../../hooks';
import useGetCompanies from '../../../hooks/get/useGetCompanies';
import { useWealthlaneCustomerApi } from '../../../hooks/useWealthlaneCustomerApi';
import { useWealthlaneAwardApi } from '../../../hooks/useWealthlaneAwardApi';
import { useWealthlaneAwardSettlementApi } from '../../../hooks/useWealthlaneAwardSettlement';
import type { ParticipantSearchResultDto } from '../../../services/wealthlane-customer-services/services/CustomerSuccessService';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import {
  ToolWrapper,
  ParticipantBar,
  ParticipantInfo,
  ActingOnBehalfBanner,
  TabBar,
  TabItem,
} from './brokerTradeTool.styled';
import SellShareSection from './SellShareSection';
import DoneTradeSection from './DoneTradeSection';
import TransactionHistorySection from './TransactionHistorySection';

function BrokerTradeTool() {
  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();
  const { handleRequest: awardHandleRequest } = useWealthlaneAwardApi();
  const { handleRequest: settlementHandleRequest } = useWealthlaneAwardSettlementApi();

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantSearchResultDto | null>(
    null
  );
  const [reasonCode, setReasonCode] = useState('');
  const [reasonCodeError, setReasonCodeError] = useState('');
  const [activeTab, setActiveTab] = useState<'sell-share' | 'done-trade'>('sell-share');

  const userAuthList = useSelector(selectAuthRoutesList);
  const policies = userAuthList?.auth?.grantedPolicies ?? {};
  const canSubmitBA = true; // !!policies['CustomerService.Customer.BrokerTradeTool.BrokerAssisted.Create'];
  const canFillDT = true; // !!policies['CustomerService.Customer.BrokerTradeTool.DoneTrade.Update'];
  const canVoid = true; // !!policies['CustomerService.Customer.BrokerTradeTool.DoneTrade.Void'];

  const { data: reasonCodeOptions, isLoading: isReasonCodesLoading } =
    useGetCsReasonCodes(awardHandleRequest);
  const { data: orderStatusOptions, isLoading: isOrderStatusLoading } =
    useGetTradeOrderStatus(settlementHandleRequest);
  const { data: companyOptions, isLoading: isCompaniesLoading } =
    useGetCompanies(customerHandleRequest);

  const { data: searchResults, isLoading: isParticipantsLoading } = useSearchParticipants(
    customerHandleRequest,
    selectedCompanyId || undefined,
    undefined,
    !!selectedCompanyId
  );

  const participantOptions = useMemo(() => {
    if (!searchResults) return [];
    return searchResults.map((p) => ({
      label: `${p.firstName} ${p.lastName} (${p.employeeId})`,
      value: p.participantId,
    }));
  }, [searchResults]);

  const handleCompanyChange = useCallback((value: string) => {
    setSelectedCompanyId(value ?? '');
    setSelectedParticipant(null);
  }, []);

  const handleParticipantChange = useCallback(
    (value: string) => {
      if (!value) {
        setSelectedParticipant(null);
        return;
      }
      const match = searchResults?.find((p) => p.participantId === value);
      if (match) {
        setSelectedParticipant(match);
      }
    },
    [searchResults]
  );

  return (
    <ToolWrapper>
      <PageHeader id='brokerTradeToolHeader'>
        <Heading variant='h3' title='Broker Trade Tool' id='pageTitle' />
        {/* <Heading
          variant='body-s'
          title='Record done trades and initiate broker-assisted orders on behalf of participants.'
          id='pageDescription'
        /> */}
      </PageHeader>

      {/* Section 1: Participant Lookup */}
      <div className='section-card'>
        <div className='section-title'>Participant Lookup</div>
        <ParticipantBar>
          <FormSelect
            type='text'
            name='company'
            id='companySelect'
            label='Company'
            placeholder='Select company...'
            options={companyOptions}
            isLoading={isCompaniesLoading}
            values={selectedCompanyId}
            arrValue={selectedCompanyId}
            isFormik={false}
            onChange={(value: string) => {
              handleCompanyChange(value);
            }}
            required
          />

          <FormSelect
            type='text'
            name='participant'
            id='participantSelect'
            label='Participant'
            placeholder={
              selectedCompanyId ? 'Search by name or employee ID...' : 'Select a company first'
            }
            options={participantOptions}
            values={selectedParticipant?.participantId ?? ''}
            arrValue={selectedParticipant?.participantId ?? ''}
            isFormik={false}
            disabled={!selectedCompanyId}
            isLoading={isParticipantsLoading}
            onChange={(value: string) => {
              handleParticipantChange(value);
            }}
            required
          />

          <div>
            <FormSelect
              type='text'
              name='reasonCode'
              id='reasonCodeSelect'
              label='Reason Code'
              placeholder='Select reason...'
              required={true}
              options={reasonCodeOptions ?? []}
              isLoading={isReasonCodesLoading}
              values={reasonCode}
              arrValue={reasonCode}
              isFormik={false}
              onChange={(value: string) => {
                setReasonCode(value);
                setReasonCodeError('');
              }}
            />
            {reasonCodeError && (
              <div style={{ color: COLORS.Red, fontSize: '12px', marginTop: '4px' }}>
                {reasonCodeError}
              </div>
            )}
          </div>
        </ParticipantBar>

        {selectedParticipant && reasonCode && (
          <ParticipantInfo>
            <div className='info-item'>
              <span className='info-label'>Company</span>
              <span className='info-value'>{selectedParticipant.companyName}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Ticker</span>
              <span className='info-value'>{selectedParticipant.ticker}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Broker</span>
              <span className='info-value'>{selectedParticipant.brokerName || '-'}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Account #</span>
              <span className='info-value'>{selectedParticipant.brokerAccountNumber || '-'}</span>
            </div>
          </ParticipantInfo>
        )}
      </div>

      {/* Acting on Behalf Of Banner */}
      {selectedParticipant && reasonCode && (
        <ActingOnBehalfBanner>
          <span className='material-symbols-outlined banner-icon'>person</span>
          <span>
            Acting on behalf of{' '}
            <span className='banner-name'>
              {selectedParticipant.firstName} {selectedParticipant.lastName}
            </span>{' '}
            ({selectedParticipant.employeeId}) — {selectedParticipant.companyName}
          </span>
        </ActingOnBehalfBanner>
      )}

      {/* Section 2: Sell Share / Done Trade (Tabbed) */}
      {selectedParticipant && reasonCode && (
        <div className='section-card'>
          <TabBar>
            <TabItem
              $active={activeTab === 'sell-share'}
              onClick={() => setActiveTab('sell-share')}>
              Sell Share
            </TabItem>
            <TabItem
              $active={activeTab === 'done-trade'}
              onClick={() => setActiveTab('done-trade')}>
              Done Trade
            </TabItem>
          </TabBar>

          <div style={{ display: activeTab === 'sell-share' ? 'block' : 'none' }}>
            <SellShareSection
              participantId={selectedParticipant.participantId}
              companyId={selectedCompanyId || undefined}
              participant={selectedParticipant}
              reasonCode={reasonCode}
              setReasonCodeError={setReasonCodeError}
              awardHandleRequest={awardHandleRequest}
              settlementHandleRequest={settlementHandleRequest}
              orderStatusOptions={orderStatusOptions}
              isOrderStatusLoading={isOrderStatusLoading}
              canSubmitBA={canSubmitBA}
              canFillDT={canFillDT}
            />
          </div>

          <div style={{ display: activeTab === 'done-trade' ? 'block' : 'none' }}>
            <DoneTradeSection
              participantId={selectedParticipant.participantId}
              selectedParticipant={selectedParticipant}
              companyId={selectedCompanyId || undefined}
              settlementHandleRequest={settlementHandleRequest}
              awardHandleRequest={awardHandleRequest}
              orderStatusOptions={orderStatusOptions}
              isOrderStatusLoading={isOrderStatusLoading}
              canFillDT={canFillDT}
            />
          </div>
        </div>
      )}

      {/* Section 4: Transaction History */}
      {selectedParticipant && reasonCode && (
        <TransactionHistorySection
          participantId={selectedParticipant.participantId}
          companyId={selectedCompanyId || undefined}
          settlementHandleRequest={settlementHandleRequest}
          canVoid={canVoid}
        />
      )}
    </ToolWrapper>
  );
}

export default BrokerTradeTool;
